<template>
  <div class="page">
    <BookingBack title="Gift Card" :textColor="'#333333'" :isCenter="true" />
    <div class="buy-content">
      <div class="loading-box">
        <div class="loading-icon">
          <van-loading type="spinner" size="48" color="#E8A740" />
        </div>
        <div class="loading-text">{{ loadingText }}</div>
      </div>
    </div>
  </div>
</template>
<script>
 import BookingBack from '@/components/Booking/back.vue'
 import { Toast } from 'vant'
 import { startStripeCheckout, getAbsoluteHashUrl } from '@/utils/stripeCheckout'
 const SS_ORDER_NO = 'giftcard_order_no'
 const SS_ORDER_ID = 'giftcard_order_id'
 const SS_CARD_ID = 'giftcard_card_id'
 const SS_PAYMENT_HANDLED = 'giftcard_payment_handled'
// For gift card checkout, Stripe adds an extra history entry:
// [detail] -> [buy] -> [stripe.com] -> [return (buy)].
// This causes in-app "back" to land on Stripe again. We fix by jumping back 2 entries
// (skipping Stripe) and then replacing the [buy] entry with the final in-app page.
const SS_GC_STRIPE_FIX_PENDING = '__giftcard_stripe_fix_pending'
const SS_GC_STRIPE_RETURN_TO = '__giftcard_stripe_return_to_fullPath'
const SS_GC_STRIPE_FIX_TS = '__giftcard_stripe_fix_ts'
const GC_STRIPE_FIX_MAX_AGE_MS = 2 * 60 * 1000
export default {
  name: 'GiftCardBuy',
  components: { BookingBack },
  data() {
    return {
      cardId: '',
      loadingText: 'Processing payment...',
      _started: false,
      _handledReturn: false
    }
  },
  created() {
    this.initFlow()
  },
  watch: {
    '$route.query': {
      handler() {
        this.initFlow()
      },
      deep: false
    }
  },
  methods: {
    safeGetSessionStorage() {
      try {
        return window.sessionStorage
      } catch (e) {
        return null
      }
    },
    tryHandleStripeFixPending() {
      const ss = this.safeGetSessionStorage()
      if (!ss) return false
      try {
        const pending = ss.getItem(SS_GC_STRIPE_FIX_PENDING)
        const returnTo = ss.getItem(SS_GC_STRIPE_RETURN_TO)
        const ts = Number(ss.getItem(SS_GC_STRIPE_FIX_TS) || 0)
        if (pending !== '1' || !returnTo) return false
        if (ts && (Date.now() - ts) > GC_STRIPE_FIX_MAX_AGE_MS) {
          ss.removeItem(SS_GC_STRIPE_FIX_PENDING)
          ss.removeItem(SS_GC_STRIPE_RETURN_TO)
          ss.removeItem(SS_GC_STRIPE_FIX_TS)
          return false
        }
        ss.removeItem(SS_GC_STRIPE_FIX_PENDING)
        ss.removeItem(SS_GC_STRIPE_RETURN_TO)
        ss.removeItem(SS_GC_STRIPE_FIX_TS)
        this.loadingText = 'Returning from payment...'
        this.$router.replace(returnTo).catch(() => {})
        return true
      } catch (e) {
        return false
      }
    },
    isStripeReferrer() {
      try {
        const ref = String(document.referrer || '')
        return /(^|\\.)stripe\\.com\\b/i.test(ref)
      } catch (e) {
        return false
      }
    },
    // Store the target route and jump back 2 entries (skipping Stripe). Once we're back on the
    // pre-payment /giftCardBuy route, initFlow() will see the pending flag and replace to returnTo.
    trySkipStripeAndReplace(returnToFullPath) {
      const ss = this.safeGetSessionStorage()
      if (!ss) return false
      try {
        if (!returnToFullPath) return false
        if (typeof window === 'undefined' || !window.history || window.history.length < 3) return false
        // We know it's a Stripe return if referrer is Stripe or giftcard_pay is present.
        if (!this.isStripeReferrer() && !this.isStripeReturn()) return false
        ss.setItem(SS_GC_STRIPE_FIX_PENDING, '1')
        ss.setItem(SS_GC_STRIPE_RETURN_TO, String(returnToFullPath || ''))
        ss.setItem(SS_GC_STRIPE_FIX_TS, String(Date.now()))
        window.history.go(-2)
        return true
      } catch (e) {
        try { ss.removeItem(SS_GC_STRIPE_FIX_PENDING) } catch (e2) {}
        try { ss.removeItem(SS_GC_STRIPE_RETURN_TO) } catch (e2) {}
        try { ss.removeItem(SS_GC_STRIPE_FIX_TS) } catch (e2) {}
        return false
      }
    },
    isStripeReturn() {
      const q = this.$route.query || {}
      return q.giftcard_pay === 'success' || q.giftcard_pay === 'cancel'
    },
    initFlow() {
      // Prefer current query; fall back to previous attempt stored before redirecting to Stripe.
      const q = this.$route.query || {}
      this.cardId = q.card_id || sessionStorage.getItem(SS_CARD_ID) || ''
      // If we just jumped back to skip Stripe, immediately replace the current buy entry with the
      // intended page (cancel/success) and stop auto-starting payment.
      if (this.tryHandleStripeFixPending()) {
        return
      }
      if (this.isStripeReturn()) {
        if (this._handledReturn) return
        this._handledReturn = true
        this.handleStripeReturn()
        return
      }
      // When Stripe return triggers our global history fix, the browser may briefly land on the
      // pre-payment /giftCardBuy route (without giftcard_pay). Avoid auto-creating a new order,
      // otherwise it will redirect back to Stripe again.
      try {
        if (sessionStorage.getItem('__stripe_fix_pending') === '1') {
          this.loadingText = 'Returning from payment...'
          return
        }
      } catch (e) {}
      // Check if we're in the middle of a Stripe redirect (marked by stripeHistoryFix)
      // This prevents auto-creating a new order when history.go(-2) lands us here
      try {
        const fromTs = Number(sessionStorage.getItem('__stripe_from_ts') || 0)
        if (fromTs && (Date.now() - fromTs) < 5 * 60 * 1000) {
          // We have a recent Stripe redirect marker - don't auto-start a new payment
          this.loadingText = 'Returning from payment...'
          return
        }
      } catch (e) {}
      // CRITICAL: If we've already handled a Stripe return, do NOT auto-create a new order.
      // This prevents the bug where returning from Stripe triggers another redirect to Stripe.
      try {
        if (sessionStorage.getItem(SS_PAYMENT_HANDLED) === '1') {
          this.loadingText = 'Payment already processed'
          // Don't auto-redirect, just stay on this page showing the message
          return
        }
      } catch (e) {}
      if (this._started) return
      this._started = true
      this.createOrderAndPay()
    },
    clearGiftCardPaySession() {
      try { sessionStorage.removeItem(SS_ORDER_NO) } catch (e) {}
      try { sessionStorage.removeItem(SS_ORDER_ID) } catch (e) {}
      try { sessionStorage.removeItem(SS_CARD_ID) } catch (e) {}
      // Note: Do NOT clear SS_PAYMENT_HANDLED here - it should persist until user explicitly retries
    },
    // Call this only when user explicitly wants to retry payment (e.g., from cancel page)
    clearPaymentHandledFlag() {
      try { sessionStorage.removeItem(SS_PAYMENT_HANDLED) } catch (e) {}
    },
    async createOrderAndPay() {
      if (!this.cardId) {
        Toast.fail('Missing gift card id')
        setTimeout(() => this.$router.go(-1), 800)
        return
      }
      try {
        this.loadingText = 'Creating order...'
        // Stripe Checkout requires absolute URLs.
        const successUrl = getAbsoluteHashUrl(`/giftCardBuy?giftcard_pay=success&card_id=${encodeURIComponent(this.cardId)}`)
        const cancelUrl = getAbsoluteHashUrl(`/giftCardBuy?giftcard_pay=cancel&card_id=${encodeURIComponent(this.cardId)}`)
        const res = await this.$api.createGiftCardOrder({
          gift_card_id: this.cardId,
          success_url: successUrl,
          cancel_url: cancelUrl
        })
        if (!res || res.code !== 1) {
          throw new Error((res && res.msg) || 'Create order failed')
        }
        const payload = res.data || {}
        const checkoutUrl = payload.checkout_url
        if (payload.order_no) {
          try { sessionStorage.setItem(SS_ORDER_NO, String(payload.order_no)) } catch (e) {}
        }
        if (payload.order_id) {
          try { sessionStorage.setItem(SS_ORDER_ID, String(payload.order_id)) } catch (e) {}
        }
        try { sessionStorage.setItem(SS_CARD_ID, String(this.cardId)) } catch (e) {}
        if (!checkoutUrl) {
          throw new Error('Missing Stripe checkout URL')
        }
        Toast.loading({ duration: 0, message: 'Redirecting to Stripe...', forbidClick: true })
        await startStripeCheckout({ checkoutUrl })
      } catch (e) {
        Toast.clear()
        Toast.fail((e && e.message) || 'Payment failed')
        this.clearGiftCardPaySession()
        setTimeout(() => this.$router.go(-1), 800)
      }
    },
    async handleStripeReturn() {
      const q = this.$route.query || {}
      const pay = q.giftcard_pay
      const cardId = q.card_id || sessionStorage.getItem(SS_CARD_ID) || ''
      const orderNo = q.order_no || sessionStorage.getItem(SS_ORDER_NO) || ''
      const orderId = q.order_id || sessionStorage.getItem(SS_ORDER_ID) || ''
      // Mark that we've handled a Stripe return - prevent any future auto-redirect
      try { sessionStorage.setItem(SS_PAYMENT_HANDLED, '1') } catch (e) {}
       if (pay === 'cancel') {
         this.clearGiftCardPaySession()
         // Clear all Stripe-related markers to prevent navigation issues
         try { sessionStorage.removeItem('__stripe_skip_fix') } catch (e) {}
         try { sessionStorage.removeItem('__stripe_from_fullPath') } catch (e) {}
         try { sessionStorage.removeItem('__stripe_from_ts') } catch (e) {}
         try { sessionStorage.removeItem('__stripe_fix_pending') } catch (e) {}
         try { sessionStorage.removeItem('__stripe_return_to_fullPath') } catch (e) {}
         try { sessionStorage.removeItem('__stripe_nav_mode') } catch (e) {}
         const cancelRoute = { path: '/giftCardCancel', query: { card_id: cardId, order_no: orderNo } }
         const cancelFullPath = this.$router.resolve(cancelRoute).route.fullPath
         // Fix history: skip Stripe and replace the pre-payment buy entry with the cancel page,
         // so back from detail won't land on Stripe again.
         if (this.trySkipStripeAndReplace(cancelFullPath)) return
         // Fallback: at least replace current return page with cancel page
         this.$router.replace(cancelRoute)
         return
       }
      this.loadingText = 'Checking payment status...'
      // Clear stripe skip flag - we've handled the return
      try { sessionStorage.removeItem('__stripe_skip_fix') } catch (e) {}
      try {
        const params = {}
        if (orderId) params.order_id = orderId
        if (orderNo) params.order_no = orderNo
        const res = await this.$api.getGiftCardPayStatus(params)
        this.clearGiftCardPaySession()
        if (!res || res.code !== 1 || !res.data || !res.data.order) {
          throw new Error((res && res.msg) || 'Failed to get payment status')
        }
        const data = res.data || {}
        const order = data.order || {}
        const giftCardInstance = data.giftCardInstance || {}
        const giftCard = data.giftCard || {}
        const supplier = (giftCard && giftCard.supplier) || {}
        // Stripe already redirected back with giftcard_pay=success. Even if backend pay_status
        // is still 0 (webhook delay), do NOT auto-redirect to Stripe again.
        const successRoute = {
          path: '/giftCardSuccess',
          query: {
            id: giftCard.id || cardId,
            card_no: giftCardInstance.card_no || '',
            name: giftCard.name || '',
            price: order.order_price || giftCard.price || '',
            value: order.gift_card_value || giftCard.value || '',
            shop_name: supplier.name || '',
            shop_logo: supplier.logo && supplier.logo.file_path ? supplier.logo.file_path : '',
            scope: giftCard.applicable_scope || 0,
            order_no: order.order_no || orderNo || '',
            pay_time: order.pay_time || '',
            pay_status: typeof order.pay_status === 'undefined' ? '' : String(order.pay_status)
          }
        }
        const successFullPath = this.$router.resolve(successRoute).route.fullPath
        if (this.trySkipStripeAndReplace(successFullPath)) return
        // Fallback: use replace so user doesn't go back to a return page that could lead to Stripe.
        this.$router.replace(successRoute)
      } catch (e) {
        this.clearGiftCardPaySession()
        Toast.fail((e && e.message) || 'Failed to verify payment')
        setTimeout(() => {
          this.$router.push({ path: '/giftCardCancel', query: { card_id: cardId, order_no: orderNo } })
        }, 300)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.page {
  min-height: 100vh;
  background: #fff;
}
.buy-content {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 6rem;
}
.loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(180deg, #fff8e8 0%, #ffffff 100%);
  border-radius: 1rem;
  min-width: 200px;
  .loading-icon {
    width: 80px;
    height: 80px;
    background: #e8a740;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
    :deep(.van-loading) {
      color: #fff !important;
    }
    :deep(.van-loading__spinner) {
      color: #fff !important;
    }
  }
  .loading-text {
    font-size: 1rem;
    color: #333;
    font-weight: 500;
  }
}
</style>
